<template>
  <v-sheet
    class="pa-4 mx-auto overflow-y-auto"
    elevation="1"
    max-width="800"
    rounded
    width="100%"
    :max-height="cardHeight"
  >
    <v-form v-model="valid">
      <v-container :max-height="cardHeight">
        <v-row>
          <v-col cols="12">
            <h6 class="text-overline mx-2">{{ $t("labels.connection") }}</h6>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="_measure.name"
              :counter="20"
              :rules="nameRules"
              :label="$t('labels.name')"
              required
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              :disabled="measureId ? true : false"
              v-model="selectedType"
              :items="types"
              :rules="requiredRule"
              :label="$t('labels.type')"
              required
              variant="outlined"
              density="compact"
              item-title="title"
              hide-details="auto"
              return-object
            ></v-select>
          </v-col>
          <v-col cols="6" md="5">
            <v-select
              :disabled="contextTypes.length <= 1"
              v-model="_measure.context"
              :items="contextTypes"
              :rules="requiredRule"
              :label="$t('labels.context')"
              required
              variant="outlined"
              density="compact"
              :hide-details="'auto'"
              return-object
            ></v-select>
          </v-col>
          <v-col cols="12" md="12">
            <v-textarea
              v-model="_measure.description"
              :label="$t('labels.description')"
              row-height="15"
              rows="1"
              variant="outlined"
              auto-grow
              density="compact"
              hide-details="auto"
            ></v-textarea>
          </v-col>
          <v-col cols="6" md="3" v-if="selectedType == 'rest'">
            <v-select
              v-model="_measure.source.spec.method"
              :items="methods"
              :rules="requiredRule"
              :label="$t('labels.method')"
              required
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="protocol"
              :items="protocols"
              :rules="requiredRule"
              :label="$t('labels.protocol')"
              required
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="6" md="3" v-if="selectedType != 'rest'">
            <v-text-field
              v-model="_measure.source.port"
              label="Port"
              variant="outlined"
              density="compact"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="uriWithPrefix"
              label="URL"
              required
              variant="outlined"
              density="compact"
              :rules="requiredRule"
              hide-details="auto"
              :prefix="uriPrefix(selectedType)"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-switch
              v-model="switchStatus"
              :label="`${$t('strings.private-connection')} ${switchStatus ? $t('labels.yes') : $t('labels.no')}`"
              hide-details="auto"
              color="secondary"
              @click="toggleSwitch"
              :disabled="_measure.source.type == 'influx'"
            ></v-switch>
            <p v-if="switchStatus" class="text-caption text-primary">
              {{ $t("strings.private-advice-policy") }}
            </p>
          </v-col>
          <v-col cols="6" class="d-flex align-center">
            <v-row v-if="switchStatus" dense>
              <v-col cols="12" md="12">
                <v-text-field
                  v-if="_measure.source.type !== 'influx'"
                  v-model="_measure.source.auth.username"
                  :label="$t('labels.username')"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                ></v-text-field>
                <v-text-field
                  v-if="_measure.source.type == 'influx'"
                  v-model="_measure.source.auth.organization"
                  :label="$t('labels.organization')"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="12">
                <v-text-field
                  v-if="_measure.source.type !== 'influx'"
                  v-model="_measure.source.auth.accessKey"
                  :label="$t('labels.access-Key')"
                  :append-inner-icon="
                    passwordVisible ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :type="passwordVisible ? 'text' : 'password'"
                  density="compact"
                  placeholder="Enter your password"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  hide-details="auto"
                  @click:append-inner="passwordVisible = !passwordVisible"
                ></v-text-field>
                <v-text-field
                  v-if="_measure.source.type == 'influx'"
                  v-model="_measure.source.auth.token"
                  :label="$t('labels.token')"
                  :append-inner-icon="tokenVisible ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="tokenVisible ? 'text' : 'password'"
                  density="compact"
                  placeholder="Enter your token"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  hide-details="auto"
                  @click:append-inner="tokenVisible = !tokenVisible"
                ></v-text-field>
              </v-col>
            </v-row>
            <p v-else class="text-caption text-primary">
              {{ $t("strings.private-message") }}
            </p>
          </v-col>
          <v-divider class="mb-4"></v-divider>
          <v-col cols="12">
            <v-row v-if="selectedType == 'rest'" class="px-0">
              <v-col cols="12" md="12">
                <h6 class="text-overline mx-2 mb-4">Headers</h6>
                <v-row
                  v-for="(header, index) in headersRow"
                  :key="index"
                  class="mb-2"
                  dense
                >
                  <v-col cols="5">
                    <v-text-field
                      v-model="header.key"
                      @input="onKeyChange(index, $event.target.value)"
                      label="Key"
                      variant="outlined"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="header.value"
                      @input="onValueChange(index, $event.target.value)"
                      label="Value"
                      color="primary"
                      variant="outlined"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="1" class="d-flex justify-center">
                    <v-btn
                      @click="removeRow(index)"
                      color="secondary"
                      class="my-1"
                      icon="mdi-delete"
                      variant="text"
                      size="default"
                      :ripple="false"
                      density="compact"
                      :disabled="
                        header.key && header.value && headersRow.length <= 1
                      "
                    >
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                cols="12"
                md="12"
                v-if="_measure.source.spec.method == 'POST'"
              >
                <v-textarea
                  v-model="_measure.source.spec.body"
                  label="Body"
                  hide-details="auto"
                  row-height="15"
                  rows="1"
                  variant="outlined"
                  density="compact"
                  auto-grow
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row v-else class="px-0">
              <v-col cols="12" md="6">
                <v-text-field
                  v-if="_measure.source.type !== 'influx'"
                  v-model="_measure.source.auth.databaseName"
                  :label="$t('labels.database-name')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-text-field>
                <v-text-field
                  v-if="_measure.source.type == 'influx'"
                  v-model="_measure.source.auth.bucket"
                  :label="$t('labels.bucket')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="12" v-if="_measure.source.type == 'mongodb'">
                <v-text-field
                  v-model="_measure.source.spec.collectionName"
                  :label="$t('labels.collection-name')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                md="12"
                v-else-if="_measure.source.type == 'graphdb'"
              >
                <v-textarea
                  v-model="_measure.source.spec.query"
                  label="Query"
                  required
                  row-height="15"
                  rows="1"
                  variant="outlined"
                  density="compact"
                  auto-grow
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-textarea>
              </v-col>
              <v-col
                cols="12"
                md="12"
                v-else-if="_measure.source.type == 'neo4j'"
              >
                <v-textarea
                  v-model="queryWithMatchPrefix"
                  label="Query"
                  required
                  row-height="15"
                  rows="1"
                  variant="outlined"
                  density="compact"
                  auto-grow
                  :rules="requiredRule"
                  hide-details="auto"
                  prefix="MATCH"
                  placeholder="... RETURN..."
                ></v-textarea>
              </v-col>
              <v-col cols="12" md="12" v-else>
                <v-textarea
                  v-model="queryWithSelectPrefix"
                  label="Query"
                  required
                  row-height="15"
                  rows="1"
                  variant="outlined"
                  density="compact"
                  auto-grow
                  :rules="requiredRule"
                  hide-details="auto"
                  prefix="SELECT"
                  placeholder="... FROM ... WHERE ..."
                ></v-textarea>
              </v-col>
            </v-row>
          </v-col>
          <v-divider
            class="mt-4"
            v-if="selectedType == 'rest' || selectedType == 'mongodb'"
          ></v-divider>
          <v-col
            cols="12"
            md="12"
            v-if="selectedType == 'rest' || selectedType == 'mongodb'"
          >
            <h6 class="text-overline mx-2 mb-4">
              {{ $t("labels.path-data") }}
            </h6>
            <p class="text-caption text-primary mt-4 mb-4">
              {{ $t("strings.compile-json-path") }}
            </p>
            <v-text-field
              v-model="jsonPathWithPrefix"
              :disabled="disabledJsonPath"
              readonly
              label="JsonPath"
              variant="outlined"
              density="compact"
              hide-details="auto"
              prefix="$."
            >
              <template #append-inner>
                <v-tooltip location="bottom">
                  <template #activator="{ props }">
                    <v-btn
                      icon
                      size="x-small"
                      color="primary"
                      v-bind="props"
                      :disabled="disabledJsonPath"
                      @click="openDialogJsonProperty"
                    >
                      <v-icon size="large">mdi-code-json</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("strings.json-property") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <DialogConfirmJsonpathDeletion
      :dialog="dialogConfirmJsonpathDeletion"
      @deleteConfirm="deleteJsonpathConfirm"
      @closeDialog="
        dialogConfirmJsonpathDeletion = !dialogConfirmJsonpathDeletion
      "
    />
  </v-sheet>
</template>

<script>
import DialogConfirmJsonpathDeletion from "./DialogConfirmJsonpathDeletion.vue";

export default {
  props: [
    "measure",
    "measureTypes",
    "containerHeight",
    "measureId",
    "contexts",
  ],
  components: { DialogConfirmJsonpathDeletion },
  data: function () {
    return {
      valid: true,
      passwordVisible: false,
      tokenVisible: false,
      methods: ["GET", "POST"],
      selectedMethod: null,
      nameRules: [
        (value) => {
          if (value) return true;
          return this.$t("strings.name-required");
        },
        (value) => {
          if (value?.length <= 20) return true;
          return this.$t("strings.name-less-20-chars");
        },
      ],
      requiredRule: [
        (value) => {
          if (value) return true;
          return this.$t("strings.value-required");
        },
      ],
      headersRow: [{ key: "", value: "" }], // Headers for REST type, starts with one empty row
      switchStatus: false,
      protocol: "HTTPS",
      protocols: ["HTTPS", "HTTP"],
      dialogConfirmJsonpathDeletion: false,
      confirmJsonpathDeletion: undefined,
    };
  },

  mounted() {
    // Set the initial state of the switch after the component has been mounted
    this.setCredentialsSwitchStatus();
    this.checkFormValidity();
  },
  watch: {
    "$i18n.locale": {
      handler: function () {
        if (!this._measure.id) {
          this._measure.name = this.$t("strings.new-measure");
        }
      },
      immediate: true,
    },

    protocol(newProtocol) {
      // When the protocol changes, update the URI
      if (this._measure.source.uri) {
        // Remove the current prefix and apply the new prefix
        const cleanedUri = this._measure.source.uri.replace(
          /^(http|https|mysql|postgres|bolt|bolt\+s|neo4j|neo4j\+s|mongodb):\/\//,
          "",
        );
        this._measure.source.uri = `${newProtocol.toLowerCase()}://${cleanedUri}`;
      }
      this.checkMandatoryFieldsForJsonpath();
    },
    measure: {
      handler() {
        if (
          this._measure.source.spec.headers &&
          this._measure.source.spec.headers !== "" &&
          this._measure.id
        ) {
          this.parseHeaders(this._measure.source.spec.headers);
        }
      },
      immediate: true,
      deep: true,
    },
    selectedType(newType, oldType) {
      this.setProtocols(newType);
      if (newType !== oldType) {
        if (newType == "rest" || newType == "mongodb") {
          this.checkMandatoryFieldsForJsonpath();
        } else {
          this.jsonPathWithPrefix = "";
        }

        if (newType !== "rest") {
          delete this._measure.source.spec.method;
          this._measure.source.uri = "";
          this.headersRow = [{ key: "", value: "" }];
        }
        if (newType !== "mongodb") {
          delete this._measure.source.spec.jsonPath;
          this._measure.source.auth.databaseName = "";
          this._measure.source.spec.collectionName = "";
        }
        if (newType !== "influx") {
          delete this._measure.source.spec.organization;
          delete this._measure.source.spec.bucket;
        }
        if (newType == "influx") {
          this.switchStatus = true;
        }
      }
    },

    "_measure.name": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.context": function () {
      this.checkMandatoryFieldsForJsonpath();
    },
    "_measure.source.spec.method": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.source.uri": function () {
      this.checkFormValidity();
    },
    "_measure.source.auth.username": function () {
      this.checkMandatoryFieldsForJsonpath();
    },
    "_measure.source.auth.accessKey": function () {
      this.checkMandatoryFieldsForJsonpath();
    },
    "_measure.source.auth.databaseName": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.source.spec.query": function () {
      this.checkFormValidity();
    },
    "_measure.source.spec.collectionName": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    uriWithPrefix: function () {
      this.checkMandatoryFieldsForJsonpath();
    },
  },
  computed: {
    types() {
      return this.measureTypes || [];
    },
    contextTypes() {
      return this.contexts || [];
    },
    // Calculate the card height based on the container height
    cardHeight() {
      return "calc(" + this.containerHeight + " - 64px - 40px)";
    },
    // Proxy to the measure prop to make it reactive
    _measure() {
      return this.measure;
    },
    // Determine the selected type of measure
    selectedType: {
      get() {
        return this._measure.source.type;
      },
      set(newValue) {
        this._measure.source.type = newValue.value.toLowerCase();
      },
    },
    // Computed property to handle the prefix for JsonPath
    jsonPathWithPrefix: {
      get() {
        // Removes the prefix from the value for display
        return this._measure.source.spec.jsonPath?.replace(/^\$\./, "");
      },
      set(newValue) {
        // Adds the prefix to the value when it is updated
        this._measure.source.spec.jsonPath = `$.${newValue}`;
      },
    },
    disabledJsonPath() {
      if (
        !this.selectedType ||
        !this._measure.name ||
        !this._measure.context ||
        !this.protocol ||
        !this.uriWithPrefix
      ) {
        return true;
      }

      if (this.selectedType != "rest" && this.selectedType != "mongodb") {
        return true;
      }

      if (
        this.selectedType == "mongodb" &&
        (!this._measure.source.spec.collectionName ||
          !this._measure.source.auth.databaseName)
      ) {
        return true;
      }

      if (this.selectedType == "rest" && !this._measure.source.spec.method) {
        return true;
      }

      if (
        this.switchStatus &&
        (!this._measure.source.auth.username ||
          !this._measure.source.auth.accessKey)
      ) {
        return true;
      }

      return false;
    },

    // Computed property to handle the prefix for the 'query' field
    queryWithSelectPrefix: {
      get() {
        // Remove the prefix from the value for display
        return this._measure.source.spec.query.replace(/^SELECT\s/, "");
      },
      set(newValue) {
        // Add the prefix to the value when it is updated
        this._measure.source.spec.query = `SELECT ${newValue}`;
      },
    },

    // Computed property to handle the prefix for the 'query' field
    queryWithMatchPrefix: {
      get() {
        // Remove the prefix from the value for display
        return this._measure.source.spec.query.replace(/^MATCH\s/, "");
      },
      set(newValue) {
        // Add the prefix to the value when it is updated
        this._measure.source.spec.query = `MATCH ${newValue}`;
      },
    },

    // Computed property to handle the URI prefix
    uriWithPrefix: {
      get() {
        return this._measure.source.uri?.replace(
          /(http|https|mysql|postgres|bolt|bolt\+s|neo4j|neo4j\+s|mongodb):\/\//g, // Replace all occurrences
          "",
        );
      },
      set(newValue) {
        // Ensure only one prefix is added, by first removing any existing prefix
        const cleanedValue = newValue.replace(
          /(http|https|mysql|postgres|bolt|bolt\+s|neo4j|neo4j\+s|mongodb):\/\//g,
          "",
        );
        this._measure.source.uri = `${this.uriPrefix()}${cleanedValue}`;
        // this._measure.source.uri = `${cleanedValue}`;
      },
    },
  },

  methods: {
    checkHideDetails() {
      return this.nameRules.length === 0 && this.requiredRule.length === 0;
    },

    setProtocols(type) {
      switch (type) {
        case "postgres":
          this.protocols = ["HTTPS", "HTTP", "POSTGRES"];
          break;
        case "neo4j":
          this.protocols = ["BOLT", "BOLT+S", "NEO4J", "NEO4J+S"];
          break;
        case "mongodb":
          this.protocols = ["TCP"];
          break;
        case "mysql":
          this.protocols = ["HTTPS", "HTTP", "MYSQL"];
          break;
        default:
          this.protocols = ["HTTPS", "HTTP"];
          break;
      }
      this.protocol = this.protocols[0];
    },

    uriPrefix() {
      const type = this._measure.source?.type;
      if (this.protocol === "TCP") {
        switch (type) {
          case "mongodb":
            return "mongodb://";
        }
      } else if (this.protocol === "BOLT") {
        return "bolt://";
      } else if (this.protocol === "NEO4J") {
        return "neo4j://";
      } else if (this.protocol === "HTTP") {
        return "http://";
      } else if (this.protocol === "HTTPS") {
        return "https://";
      } else if (this.protocol === "POSTGRES") {
        return "postgres://";
      } else if (this.protocol === "BOLT+S") {
        return "bolt+s://";
      } else if (this.protocol === "NEO4J+S") {
        return "neo4j+s://";
      } else if (this.protocol === "MYSQL") {
        return "mysql://";
      }
    },

    // Checks if all required Form fields are filled
    checkFormValidity() {
      // Remove the protocol prefix from the URI for validation
      const uriWithoutProtocol = this._measure.source.uri?.replace(
        this.uriPrefix(),
        "",
      );
      // A form is considered valid if it has a name, a context, a selected type, a URI and a protocol
      let isValid =
        this._measure.name &&
        this._measure.context &&
        this.selectedType &&
        uriWithoutProtocol;
      // Define an object to map types to required fields
      const typeFields = {
        rest: ["method"],
        mongodb: ["collectionName", "databaseName"],
        default: ["query", "databaseName"],
        influx: ["organization", "bucket", "query", "token"],
      };
      // Get the required fields for the selected type
      const requiredFields =
        typeFields[this.selectedType] || typeFields["default"];
      // Check if all required fields are present
      requiredFields.forEach((field) => {
        isValid =
          isValid &&
          (this._measure.source.spec[field] ||
            this._measure.source.auth[field]);
      });
      // Emits an event to the parent component to indicate the validity of the form
      this.$emit("form-validity-changed", isValid);
    },

    // Method to add a new empty row if the last Header row is filled
    addRow() {
      const lastRow = this.headersRow[this.headersRow.length - 1];
      if (lastRow.key && lastRow.value) {
        this.headersRow.push({ key: "", value: "" });
      }
      this.transformHeaders(this.headersRow);
    },
    // Method to remove a Header row at a specific index
    removeRow(index) {
      this.headersRow.splice(index, 1);
      this.transformHeaders(this.headersRow);
      // Add an empty row if all rows have been removed
      if (this.headersRow.length === 0) {
        this.headersRow.push({ key: "", value: "" });
      }
    },
    // Method called when the Header key input changes
    onKeyChange(index, value) {
      this.headersRow[index].key = value;
      this.addRow();
    },
    // Method called when the Header value input changes
    onValueChange(index, value) {
      this.headersRow[index].value = value;
      this.addRow();
    },
    /**
     * This method transforms an array of header objects into a string.
     * The resulting string is a comma-separated list of "key:value" pairs.
     * Empty keys and values are ignored.
     * The transformed string is assigned to _measure.source.spec.headers.
     *
     * @param {Array} headersRow - The array of header objects to transform.
     */
    transformHeaders(headersRow) {
      let transformedHeaders = [];
      headersRow.forEach((header) => {
        // Check if both key and value are not empty
        if (header.key?.trim() !== "" && header.value?.trim() !== "") {
          let transformedHeader = `${header.key}:${header.value}`;
          transformedHeaders.push(transformedHeader);
        }
      });
      let headersString = transformedHeaders.join(", ");
      // Assign the transformed headers string to _measure.source.spec.headers
      this._measure.source.spec.headers = headersString;
    },

    parseHeaders(headersString) {
      // Split the headers string into an array of "key:value" strings
      let headersArray = headersString.split(", ");
      // Map each "key:value" string to an object with 'key' and 'value' properties
      let headersObjects = headersArray.map((headerString) => {
        let [key, value] = headerString.split(":");
        return { key, value };
      });
      // Add an empty object to the end of the array
      headersObjects.push({ key: "", value: "" });
      // Assign the array of header objects to headersRow
      this.headersRow = headersObjects;
    },

    // Method to toggle the switch status
    toggleSwitch() {
      this.switchStatus = !this.switchStatus;
      if (this.switchStatus) {
        // Clear the authentication fields when the switch is turned on
        this._measure.source.auth.username = "";
        this._measure.source.auth.organization = "";
        this._measure.source.auth.accessKey = "";
        this._measure.source.auth.token = "";
      } else {
        // Restore the authentication fields when the switch is turned off
        this._measure.source.auth.username =
          this.measure.source.auth?.username || "";
        this._measure.source.auth.organization =
          this.measure.source.auth?.organization || "";
        this._measure.source.auth.accessKey =
          this.measure.source.auth?.accessKey || "";
        this._measure.source.auth.token = this.measure.source.auth?.token || "";
      }
    },
    // Method to set credentials switch status according to BE data
    setCredentialsSwitchStatus() {
      if (this.measure.source.auth == null) {
        this._measure.source.auth = {
          username: "",
          organization: "",
          accessKey: "",
          token: "",
          secretPath: "",
        };
      }
      this.switchStatus = !!(
        (this.measure.source.auth?.username &&
          this.measure.source.auth?.accessKey) ||
        this.measure.source.auth?.secretPath
      );
    },

    checkMandatoryFieldsForJsonpath() {
      if (!this.confirmJsonpathDeletion && this.jsonPathWithPrefix) {
        this.openDialogDeleteJsonpathConfirm();
      }
    },

    openDialogJsonProperty() {
      this.confirmJsonpathDeletion = undefined;
      this.$emit("openDialogJsonProperty");
    },

    openDialogDeleteJsonpathConfirm() {
      this.dialogConfirmJsonpathDeletion = true;
    },
    deleteJsonpathConfirm(val) {
      this.confirmJsonpathDeletion = val ? "confirm" : "no-confirm";
      if (val) {
        this.jsonPathWithPrefix = "";
      }
    },
  },
};
</script>
